import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Table,
  Button,
  Select,
  Switch,
  Checkbox,
  Popconfirm,
  Spin,
  Divider,
  Typography,
  Empty,
  message,
} from 'antd';
import { PlusCircleOutlined, DeleteOutlined, SyncOutlined } from '@ant-design/icons';
import {
  getElationRouting,
  updateElationRouting,
  refreshElationRouting,
} from '../../../actions/elationRouting';
import { captureAnalytics } from '../../../actions/preload';
import dedupeDocumentCategories from '../../../utils/dedupeDocumentCategories';
import './manage-elation-routing.scss';

const { Option, OptGroup } = Select;
const { Paragraph, Text } = Typography;

// Select values encode a stored target ({ type, id }) as "user:<id>" or
// "group:<id>".
export const targetToValue = (target) => {
  if (!target || typeof target !== 'object') return undefined;
  if ((target.type === 'user' || target.type === 'group') && target.id) return `${target.type}:${target.id}`;
  return undefined;
};

export const valueToTarget = (value) => {
  if (!value) return null;
  const [type, ...rest] = value.split(':');
  return { type, id: rest.join(':') };
};

const fixedTarget = (type, id) => (type ? targetToValue({ type, id }) : undefined);

// Flattens the stored per_category / per_department maps into editable rows of
// { department, category, target }. The org-wide default is edited separately.
export const delegationToRules = (delegation, buildKey) => {
  const rules = [];
  Object.entries(delegation?.per_category || {}).forEach(([category, target]) => {
    rules.push({
      key: buildKey(), department: null, category, target: targetToValue(target),
    });
  });
  Object.entries(delegation?.per_department || {}).forEach(([department, config]) => {
    const departmentId = Number(department);
    const departmentDefault = fixedTarget(config?.default_type, config?.default_id);
    if (departmentDefault) {
      rules.push({
        key: buildKey(), department: departmentId, category: null, target: departmentDefault,
      });
    }
    Object.entries(config?.per_category || {}).forEach(([category, target]) => {
      rules.push({
        key: buildKey(), department: departmentId, category, target: targetToValue(target),
      });
    });
  });
  return rules;
};

const ruleScopeKey = (rule) => `${rule.department ?? '*'}|${rule.category ?? '*'}`;

const departmentName = (departments, id) => (
  departments.find((department) => department.id === id)?.name || `Department #${id}`
);

// Groups rules for display: org-wide category rules first, then one group per
// department (by name). Within a group, category rules sort by name and a
// department's "any other category" rule comes last.
export const groupRules = (rules, departments) => {
  const groups = new Map();
  rules.forEach((rule) => {
    const id = rule.department ?? null;
    if (!groups.has(id)) groups.set(id, []);
    groups.get(id).push(rule);
  });
  const byCategory = (a, b) => {
    if (!a.category && !b.category) return 0;
    if (!a.category) return 1;
    if (!b.category) return -1;
    return a.category.localeCompare(b.category);
  };
  return [...groups.entries()]
    .map(([department, groupRuleList]) => ({
      department,
      name: department == null ? null : departmentName(departments, department),
      // groupRuleList is built fresh above, so sorting it in place is safe.
      rules: groupRuleList.sort(byCategory),
    }))
    .sort((a, b) => {
      if (a.department == null) return -1;
      if (b.department == null) return 1;
      return a.name.localeCompare(b.name);
    });
};

// A rule needs a delegate and at least one of department / category (both
// blank would just be the default), and two rules can't share a scope.
export const invalidRuleKeys = (rules) => {
  const seen = {};
  const invalid = new Set();
  rules.forEach((rule) => {
    const scope = ruleScopeKey(rule);
    if (!rule.target || (rule.department == null && !rule.category) || seen[scope]) invalid.add(rule.key);
    seen[scope] = true;
  });
  return invalid;
};

const splitTarget = (value) => {
  const target = valueToTarget(value);
  return { type: target?.type || null, id: target?.id || null };
};

// Rebuilds the stored shape from the editable form state. Physician delegates
// and the report-physician option only persist while routeByProvider is on.
export const buildDelegationPayload = ({
  enabled, defaultTarget, rules, routeByProvider, physicianTargets, orderingProviderAsPhysician,
}) => {
  const perCategory = {};
  const perDepartment = {};
  rules.forEach((rule) => {
    const target = valueToTarget(rule.target);
    // Unfinished rows (no delegate yet, or an org-wide row with no category)
    // aren't saved; Save is blocked until they're completed or removed.
    if (!target || (rule.department == null && !rule.category)) return;
    if (rule.department == null) {
      perCategory[rule.category] = target;
      return;
    }
    const config = perDepartment[rule.department] || { per_category: {} };
    if (rule.category) {
      config.per_category[rule.category] = target;
    } else {
      config.default_type = target.type;
      config.default_id = target.id;
    }
    perDepartment[rule.department] = config;
  });

  const perPhysician = {};
  if (routeByProvider) {
    Object.entries(physicianTargets).forEach(([physicianId, value]) => {
      if (value) perPhysician[physicianId] = valueToTarget(value);
    });
  }

  const defaultParts = splitTarget(defaultTarget);
  return {
    enabled,
    default_type: defaultParts.type,
    default_id: defaultParts.id,
    per_category: perCategory,
    per_department: perDepartment,
    per_physician: perPhysician,
    ordering_provider_as_physician: Boolean(routeByProvider && orderingProviderAsPhysician),
  };
};

// Elation panel of the EHR Routing settings tab. Admins choose who uploaded
// reports are delegated to in Elation: each ordering provider's delegate (e.g.
// their MA), category/department exceptions, and an optional default. Config is
// stored as JSON on the Elation credential and saved wholesale.
export class ManageElationRouting extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      refreshing: false,
      credentialId: null,
      elationCredentials: [],
      physicians: [],
      users: [],
      staffGroups: [],
      documentCategories: [],
      departments: [],
      syncedAt: null,
      enabled: false,
      defaultTarget: undefined,
      // rules: [{ key, department, category, target }]
      rules: [],
      // routeByProvider: whether the ordering-provider section is switched on
      routeByProvider: false,
      // physicianTargets: { [elation physician id]: select value }
      physicianTargets: {},
      orderingProviderAsPhysician: false,
    };
    this.componentMounted = true;
    this.loadSequence = 0;
    this.ruleKeySequence = 0;
    // Serialized snapshot of the last loaded/saved form, so Save can be
    // disabled while the form is pristine.
    this.savedSnapshot = null;
  }

  async componentDidMount() {
    await this.loadRouting();
  }

  componentWillUnmount() {
    this.componentMounted = false;
    this.loadSequence += 1;
  }

  buildRuleKey = () => {
    this.ruleKeySequence += 1;
    return `elation-rule-${this.ruleKeySequence}`;
  };

  applyResponse = (data, credentialId) => {
    const delegation = data.delegation || {};
    const physicianTargets = {};
    Object.entries(delegation.per_physician || {}).forEach(([physicianId, target]) => {
      physicianTargets[physicianId] = targetToValue(target);
    });
    const form = {
      enabled: delegation.enabled === true,
      defaultTarget: fixedTarget(delegation.default_type, delegation.default_id),
      rules: delegationToRules(delegation, this.buildRuleKey),
      routeByProvider: Object.keys(physicianTargets).length > 0
        || delegation.ordering_provider_as_physician === true,
      physicianTargets,
      orderingProviderAsPhysician: delegation.ordering_provider_as_physician === true,
    };
    this.savedSnapshot = this.snapshot(form);
    this.setState({
      ...form,
      credentialId: data.emr_integration_credential_id || credentialId || null,
      elationCredentials: data.elation_credentials || [],
      physicians: data.physicians || [],
      users: data.users || [],
      staffGroups: data.staff_groups || [],
      documentCategories: data.document_categories || [],
      departments: data.departments || [],
      syncedAt: data.synced_at || null,
    });
  };

  loadRouting = async (credentialId) => {
    this.loadSequence += 1;
    const sequence = this.loadSequence;
    this.setState({ loading: true });
    try {
      const response = await this.props.getElationRouting(credentialId);
      if (!this.componentMounted || sequence !== this.loadSequence) return;

      if (response?.error) {
        message.error('Unable to load Elation routing settings');
        return;
      }
      this.applyResponse(response?.data || {}, credentialId);
    } catch {
      if (this.componentMounted && sequence === this.loadSequence) {
        message.error('Unable to load Elation routing settings');
      }
    } finally {
      if (this.componentMounted && sequence === this.loadSequence) {
        this.setState({ loading: false });
      }
    }
  };

  handleCredentialChange = async (credentialId) => {
    captureAnalytics('elation_routing_credential_changed');
    await this.loadRouting(credentialId);
  };

  handleRefresh = async () => {
    captureAnalytics('elation_routing_refresh_clicked');
    const { credentialId } = this.state;
    const sequence = this.loadSequence;
    this.setState({ refreshing: true });
    try {
      const response = await this.props.refreshElationRouting(credentialId);
      // Ignore a late response once the admin has switched practices or reloaded.
      if (!this.componentMounted || sequence !== this.loadSequence || credentialId !== this.state.credentialId) return;

      if (response?.error) {
        message.error(response?.response?.data?.error || 'Unable to refresh from Elation');
        return;
      }
      // Only the pick-lists change on refresh; keep any unsaved edits.
      const data = response?.data || {};
      this.setState({
        physicians: data.physicians || [],
        users: data.users || [],
        staffGroups: data.staff_groups || [],
        syncedAt: data.synced_at || null,
      });
      message.success('Refreshed from Elation');
    } catch {
      if (this.componentMounted) message.error('Unable to refresh from Elation');
    } finally {
      if (this.componentMounted) this.setState({ refreshing: false });
    }
  };

  // --- Dirty helpers ---------------------------------------------------------

  formState = () => {
    const {
      enabled, defaultTarget, rules, routeByProvider, physicianTargets, orderingProviderAsPhysician,
    } = this.state;
    return {
      enabled, defaultTarget, rules, routeByProvider, physicianTargets, orderingProviderAsPhysician,
    };
  };

  // Compares what would be saved, so switching the ordering-provider section on
  // without mapping anyone isn't an unsaved change.
  snapshot = (form) => JSON.stringify(buildDelegationPayload(form));

  isDirty = () => this.savedSnapshot !== null && this.snapshot(this.formState()) !== this.savedSnapshot;

  // --- Rules -----------------------------------------------------------------

  // department: null for an org-wide category rule, or a department id.
  addRule = (department = null) => {
    captureAnalytics('elation_routing_rule_added', { department_scoped: department != null });
    this.setState((prev) => ({
      rules: [...prev.rules, {
        key: this.buildRuleKey(), department, category: undefined, target: undefined,
      }],
    }));
  };

  updateRule = (key, patch) => {
    captureAnalytics('elation_routing_rule_updated', { fields: Object.keys(patch) });
    this.setState((prev) => ({
      rules: prev.rules.map((rule) => (rule.key === key ? { ...rule, ...patch } : rule)),
    }));
  };

  removeRule = (key) => {
    captureAnalytics('elation_routing_rule_removed');
    this.setState((prev) => ({ rules: prev.rules.filter((rule) => rule.key !== key) }));
  };

  updateDefaultTarget = (value) => {
    captureAnalytics('elation_routing_default_delegate_changed', { cleared: !value });
    this.setState({ defaultTarget: value });
  };

  updateOrderingProviderAsPhysician = (checked) => {
    captureAnalytics('elation_routing_file_under_provider_toggled', { enabled: checked });
    this.setState({ orderingProviderAsPhysician: checked });
  };

  updatePhysicianTarget = (physicianId, value) => {
    captureAnalytics('elation_routing_physician_mapped', { cleared: !value });
    this.setState((prev) => ({
      physicianTargets: { ...prev.physicianTargets, [physicianId]: value },
    }));
  };

  // --- Save ------------------------------------------------------------------

  handleSave = async () => {
    const invalid = invalidRuleKeys(this.state.rules);
    if (invalid.size > 0) {
      message.error('Finish or remove the highlighted rules before saving');
      return;
    }
    const delegation = buildDelegationPayload(this.formState());
    captureAnalytics('elation_routing_settings_save_clicked', {
      enabled: delegation.enabled,
      rule_count: this.state.rules.length,
      physician_count: Object.keys(delegation.per_physician).length,
      has_default: Boolean(delegation.default_type),
    });
    this.setState({ saving: true });
    try {
      const response = await this.props.updateElationRouting(delegation, this.state.credentialId);
      if (!this.componentMounted) return;

      if (response?.error) {
        message.error(response?.response?.data?.error || 'Unable to save Elation routing settings');
        return;
      }
      this.applyResponse(response?.data || {}, this.state.credentialId);
      message.success('Elation routing settings saved');
    } catch {
      if (this.componentMounted) message.error('Unable to save Elation routing settings');
    } finally {
      if (this.componentMounted) this.setState({ saving: false });
    }
  };

  // --- Render ----------------------------------------------------------------

  // Delegate options, with any saved id that's no longer synced kept visible
  // (flagged) so it can be seen and replaced rather than silently dropped.
  renderDelegateOptions = (value) => {
    const { users, staffGroups } = this.state;
    const known = new Set([
      ...users.map((user) => `user:${user.id}`),
      ...staffGroups.map((group) => `group:${group.id}`),
    ]);
    const missing = value && !known.has(value) ? valueToTarget(value) : null;

    return [
      missing && (
        <OptGroup key="missing" label="Not found in Elation">
          <Option value={value}>
            {`${missing.type === 'group' ? 'Staff group' : 'User'} #${missing.id}`}
          </Option>
        </OptGroup>
      ),
      <OptGroup key="users" label="Users">
        {users.map((user) => <Option key={`user:${user.id}`} value={`user:${user.id}`}>{user.name}</Option>)}
      </OptGroup>,
      <OptGroup key="groups" label="Staff groups">
        {staffGroups.map((group) => (
          <Option key={`group:${group.id}`} value={`group:${group.id}`}>{group.name}</Option>
        ))}
      </OptGroup>,
    ].filter(Boolean);
  };

  delegateSelect = ({
    value, onChange, placeholder, allowClear = true, invalid = false,
  }) => (
    <Select
      showSearch
      allowClear={allowClear}
      className={invalid ? 'elation-cell--invalid' : undefined}
      style={{ width: '100%' }}
      placeholder={placeholder}
      value={value}
      optionFilterProp="children"
      onChange={(next) => onChange(next || undefined)}
    >
      {this.renderDelegateOptions(value)}
    </Select>
  );

  ruleColumns = (group, invalid) => {
    const categoryOptions = dedupeDocumentCategories(
      this.state.documentCategories,
      (category) => category && category.value,
    );
    const departmentScoped = group.department != null;

    return [
      {
        title: 'Document category',
        dataIndex: 'category',
        width: '40%',
        render: (value, row) => {
          const usedElsewhere = group.rules.reduce((used, other) => {
            if (other.key !== row.key && other.category) used.add(other.category);
            return used;
          }, new Set());
          return (
            <Select
              showSearch
              allowClear={departmentScoped}
              className={invalid.has(row.key) && !departmentScoped && !value ? 'elation-cell--invalid' : undefined}
              style={{ width: '100%' }}
              placeholder={departmentScoped ? 'Any other category' : 'Select category'}
              value={value || undefined}
              optionFilterProp="children"
              onChange={(category) => this.updateRule(row.key, { category: category || null })}
            >
              {value && !categoryOptions.some((category) => category.value === value) && (
                <Option value={value}>{`${value} (no longer a category)`}</Option>
              )}
              {categoryOptions.map((category) => (
                <Option key={category.value} value={category.value} disabled={usedElsewhere.has(category.value)}>
                  {category.label}
                </Option>
              ))}
            </Select>
          );
        },
      },
      {
        title: 'Delegate to',
        dataIndex: 'target',
        render: (value, row) => this.delegateSelect({
          value,
          placeholder: 'Select delegate',
          allowClear: false,
          invalid: invalid.has(row.key) && !value,
          onChange: (target) => this.updateRule(row.key, { target }),
        }),
      },
      {
        title: '',
        dataIndex: 'actions',
        width: 56,
        align: 'right',
        render: (_value, row) => (
          <Popconfirm
            placement="topRight"
            title="Remove rule?"
            okText="Remove"
            okType="danger"
            onConfirm={() => this.removeRule(row.key)}
          >
            <Button type="text" danger aria-label="Remove delegation rule" icon={<DeleteOutlined />} />
          </Popconfirm>
        ),
      },
    ];
  };

  renderRuleGroup(group, invalid, showHeader) {
    return (
      <div className="elation-rule-group" key={group.department ?? 'org'}>
        {showHeader && (
          <div className="elation-rule-group__header">
            <Text strong>{group.department == null ? 'All departments' : group.name}</Text>
            <Button size="small" type="link" icon={<PlusCircleOutlined />} onClick={() => this.addRule(group.department)}>
              Add category
            </Button>
          </div>
        )}
        <Table
          rowKey="key"
          dataSource={group.rules}
          columns={this.ruleColumns(group, invalid)}
          pagination={false}
          size="small"
          bordered
          tableLayout="fixed"
          showHeader={false}
          rowClassName={(row) => (invalid.has(row.key) ? 'elation-row--invalid' : '')}
        />
      </div>
    );
  }

  renderRules(invalid) {
    const { rules, departments } = this.state;
    const groups = groupRules(rules, departments);
    const hasDepartmentGroups = groups.some((group) => group.department != null);
    const usedDepartments = new Set(groups.map((group) => group.department));
    const availableDepartments = departments.filter((department) => !usedDepartments.has(department.id));

    return (
      <>
        {groups.length === 0 && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No rules yet" />
        )}
        {groups.map((group) => this.renderRuleGroup(group, invalid, hasDepartmentGroups))}
        <div className="elation-routing__rule-actions">
          {(!hasDepartmentGroups || !usedDepartments.has(null)) && (
            <Button icon={<PlusCircleOutlined />} onClick={() => this.addRule(null)}>
              {hasDepartmentGroups ? 'Add rule for all departments' : 'Add category rule'}
            </Button>
          )}
          {departments.length > 1 && availableDepartments.length > 0 && (
            <Select
              className="elation-routing__department-picker"
              placeholder="Add rules for a department…"
              value={null}
              showSearch
              optionFilterProp="children"
              onChange={(department) => this.addRule(department)}
            >
              {availableDepartments.map((department) => (
                <Option key={department.id} value={department.id}>{department.name}</Option>
              ))}
            </Select>
          )}
        </div>
      </>
    );
  }

  renderPhysicianTable() {
    const { physicians, physicianTargets } = this.state;
    const syncedIds = new Set(physicians.map((physician) => physician.id));
    const departed = Object.keys(physicianTargets).reduce((rows, id) => {
      if (physicianTargets[id] && !syncedIds.has(id)) rows.push({ id, name: `Physician #${id}`, departed: true });
      return rows;
    }, []);

    return (
      <Table
        rowKey="id"
        dataSource={[...physicians, ...departed]}
        pagination={false}
        size="small"
        bordered
        tableLayout="fixed"
        columns={[
          {
            title: 'Ordering provider (Elation physician)',
            dataIndex: 'name',
            render: (name, physician) => (
              <span>
                {name}
                {physician.credentials && <Text type="secondary">{`, ${physician.credentials}`}</Text>}
                {physician.departed && <Text type="secondary"> (no longer in Elation, clear to remove)</Text>}
              </span>
            ),
          },
          {
            title: 'Delegate to',
            dataIndex: 'id',
            width: '50%',
            render: (id) => this.delegateSelect({
              value: physicianTargets[id],
              placeholder: 'Use the default delegate',
              onChange: (value) => this.updatePhysicianTarget(id, value),
            }),
          },
        ]}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No physicians synced from Elation yet. Use Refresh from Elation."
            />
          ),
        }}
      />
    );
  }

  render() {
    const {
      loading, saving, refreshing, credentialId, elationCredentials, syncedAt,
      enabled, defaultTarget, rules, routeByProvider, orderingProviderAsPhysician,
      users, staffGroups,
    } = this.state;
    const dirty = this.isDirty();
    const invalid = invalidRuleKeys(rules);

    return (
      // Spin while saving too, so edits can't land between the request and the
      // response that replaces the form state.
      <Spin spinning={loading || saving}>
        <div className="elation-routing">
          <div className="elation-routing__toolbar">
            {elationCredentials.length > 1 && (
              <div className="elation-routing__credential">
                <Text strong>Elation practice</Text>
                <Select
                  value={credentialId}
                  onChange={this.handleCredentialChange}
                  disabled={loading || saving || refreshing || dirty}
                  className="elation-routing__credential-select"
                >
                  {elationCredentials.map((credential) => (
                    <Option key={credential.id} value={credential.id}>{credential.name}</Option>
                  ))}
                </Select>
                {dirty && <Text type="secondary">Save changes before switching practices.</Text>}
              </div>
            )}
            <div className="elation-routing__sync">
              <Text type="secondary">
                {syncedAt
                  ? `Staff last synced from Elation ${new Date(syncedAt).toLocaleString()}`
                  : 'Staff not synced from Elation yet'}
              </Text>
              <Button icon={<SyncOutlined />} loading={refreshing} onClick={this.handleRefresh}>
                Refresh from Elation
              </Button>
            </div>
          </div>

          {!loading && users.length === 0 && staffGroups.length === 0 && (
            <Alert
              className="elation-routing__alert"
              type="warning"
              showIcon
              message="No Elation users or staff groups to delegate to yet"
              description={
                'Click "Refresh from Elation". If it keeps failing, the practice\'s Medsender API user '
                + 'in Elation likely lacks permission to view users and staff groups.'
              }
            />
          )}

          <section className="elation-routing__section">
            <h2 className="elation-routing__section-title">Report delegation</h2>
            <Paragraph type="secondary" className="elation-routing__section-desc">
              Choose who automatically uploaded reports are delegated to in Elation.
            </Paragraph>
            <div className="elation-routing__field">
              <Switch
                checked={enabled}
                onChange={(checked) => {
                  captureAnalytics('elation_routing_enabled_toggled', { enabled: checked });
                  this.setState({ enabled: checked });
                }}
              />
              <Text>Delegate reports when they&apos;re automatically uploaded</Text>
            </div>
          </section>

          <section className="elation-routing__section">
            <h3 className="elation-routing__subsection-title">Rules</h3>
            <Paragraph type="secondary" className="elation-routing__section-desc">
              Send reports to a person or staff group by document category, for the whole practice
              or for one department. A department&apos;s &quot;Any other category&quot; row covers its
              remaining categories.
            </Paragraph>
            {this.renderRules(invalid)}
          </section>

          <Divider />

          <section className="elation-routing__section">
            <h3 className="elation-routing__subsection-title">Everything else (optional)</h3>
            <Paragraph type="secondary" className="elation-routing__section-desc">
              Who reports go to when no rule matches. Leave empty to not delegate them.
            </Paragraph>
            <div className="elation-routing__default">
              {this.delegateSelect({
                value: defaultTarget,
                placeholder: 'Do not delegate',
                onChange: this.updateDefaultTarget,
              })}
            </div>
          </section>

          <Divider />

          <section className="elation-routing__section">
            <div className="elation-routing__field">
              <Switch
                checked={routeByProvider}
                onChange={(checked) => {
                  captureAnalytics('elation_routing_route_by_provider_toggled', { enabled: checked });
                  this.setState({ routeByProvider: checked });
                }}
              />
              <Text strong>Delegate by ordering provider</Text>
            </div>
            <Paragraph type="secondary" className="elation-routing__section-desc">
              Send each report to the delegate of the provider who ordered it (for example, their
              MA). Medsender matches the ordering provider on the document (or the referring provider if
              no ordering provider matches) to an Elation physician.
              Category rules still win; unmatched reports use the department or &quot;everything else&quot;
              delegate.
            </Paragraph>
            {routeByProvider && (
              <>
                {this.renderPhysicianTable()}
                <Checkbox
                  className="elation-routing__checkbox"
                  checked={orderingProviderAsPhysician}
                  onChange={(event) => this.updateOrderingProviderAsPhysician(event.target.checked)}
                >
                  Also file the report under the matched ordering provider in Elation
                </Checkbox>
              </>
            )}
          </section>

          <Divider />

          <div className="elation-routing__save-bar">
            {dirty && <Text type="secondary">You have unsaved changes</Text>}
            <Button type="primary" loading={saving} disabled={!dirty || saving} onClick={this.handleSave}>
              Save changes
            </Button>
          </div>
        </div>
      </Spin>
    );
  }
}

ManageElationRouting.propTypes = {
  getElationRouting: PropTypes.func.isRequired,
  updateElationRouting: PropTypes.func.isRequired,
  refreshElationRouting: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  getElationRouting,
  updateElationRouting,
  refreshElationRouting,
};

export default connect(null, mapDispatchToProps)(ManageElationRouting);
